/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/api/resolve.ts
 * layer: web
 * area: api
 * kind: api
 * web_root: api
 * relpath: web/src/api/resolve.ts
 * filename: resolve.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

/**
 * Resolution Queue API - Unified detect & resolve queue
 */

import { z } from 'zod';
import { api, unwrapOne } from '@/platform/api-client/client';
import { responseKeyContract } from '@/platform/api-client/responseKeyContract';

export const RESOLUTION_RESPONSE_KEYS = responseKeyContract({
  dynamicKeyPaths: [
    ['**', 'byStatus'],
    ['**', 'bySeverity'],
    ['**', 'bySource'],
  ],
  opaquePaths: [
    ['**', 'actionConfig'],
    ['**', 'actionResult'],
  ],
});

const RESPONSE_CONFIG = { responseKeyContract: RESOLUTION_RESPONSE_KEYS };

// ── Types ──

export type QueueSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';
export type QueueStatus =
  | 'open'
  | 'auto_resolved'
  | 'pending_approval'
  | 'approved'
  | 'executing'
  | 'resolved'
  | 'failed'
  | 'dismissed';
export type QueueTier = 'auto' | 'one_click' | 'assigned' | 'escalated';
export type SourceType =
  | 'device_alert'
  | 'backup'
  | 'compliance'
  | 'credential'
  | 'certificate'
  | 'patch'
  | 'vendor'
  | 'perimeter'
  | 'remediation_failed'
  | 'm365';

export interface QueueItem {
  id: string;
  tenantId: string;
  customerId?: string;
  sourceType: SourceType;
  sourceId?: string;
  sourceSubject?: string;
  title: string;
  description?: string;
  severity: QueueSeverity;
  status: QueueStatus;
  tier: QueueTier;
  actionType?: string;
  actionConfig?: Record<string, unknown>;
  actionResult?: Record<string, unknown>;
  assignedTo?: string;
  deviceId?: string;
  resolution?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  autoResolved: boolean;
  dedupKey?: string;
  createdAt: string;
  updatedAt: string;
  // Joined fields from service
  deviceHostname?: string;
  customerName?: string;
  assignedToName?: string;
}

export interface QueueStats {
  total: number;
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
  bySource: Record<string, number>;
  autoResolvedToday: number;
}

export interface P1InterruptionPolicy {
  enabled: boolean;
  priority: 'P1';
  role?: string;
  source: string;
  reason?: string;
}

export interface P1InterruptionDecision {
  policy: P1InterruptionPolicy;
  item?: QueueItem;
}

export type ResolveQueryFailureKind = 'unauthorized' | 'malformed' | 'unavailable';

export class ResolveContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ResolveContractError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseCount(value: unknown, field: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new ResolveContractError(`${field} must be a non-negative integer`);
  }
  return value as number;
}

function parseCountMap(value: unknown, field: string): Record<string, number> {
  if (!isRecord(value)) {
    throw new ResolveContractError(`${field} must be an object`);
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, count]) => [key, parseCount(count, `${field}.${key}`)])
  );
}

export function parseQueueStats(value: unknown): QueueStats {
  if (!isRecord(value)) {
    throw new ResolveContractError('resolve stats response must be an object');
  }
  const byStatus = parseCountMap(value.byStatus, 'byStatus');
  const total = parseCount(value.total, 'total');
  const statusTotal = Object.values(byStatus).reduce((sum, count) => sum + count, 0);
  if (statusTotal !== total) {
    throw new ResolveContractError('total must equal the sum of byStatus counts');
  }
  return {
    total,
    byStatus,
    bySeverity: parseCountMap(value.bySeverity, 'bySeverity'),
    bySource: parseCountMap(value.bySource, 'bySource'),
    autoResolvedToday: parseCount(value.autoResolvedToday, 'autoResolvedToday'),
  };
}

function parseQueueResponse(value: unknown): { items: QueueItem[]; total: number } {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    throw new ResolveContractError('resolve queue response must contain an items array');
  }
  return { items: value.items as QueueItem[], total: parseCount(value.total, 'total') };
}

export function classifyResolveQueryFailure(error: unknown): ResolveQueryFailureKind {
  if (error instanceof ResolveContractError) return 'malformed';
  const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
  if (status === 401 || status === 403) return 'unauthorized';
  return 'unavailable';
}

export interface QueueFilters {
  status?: QueueStatus;
  severity?: QueueSeverity;
  sourceType?: SourceType;
  assignedTo?: string;
  limit?: number;
  offset?: number;
}

// ── API Functions ──

export async function fetchQueue(
  filters?: QueueFilters
): Promise<{ items: QueueItem[]; total: number }> {
  const params: Record<string, string | number | undefined> = {};
  if (filters) {
    if (filters.status) params.status = filters.status;
    if (filters.severity) params.severity = filters.severity;
    if (filters.sourceType) params.source_type = filters.sourceType;
    if (filters.assignedTo) params.assigned_to = filters.assignedTo;
    if (filters.limit) params.limit = filters.limit;
    if (filters.offset) params.offset = filters.offset;
  }
  const response = await api.get('/resolve', { params, ...RESPONSE_CONFIG });
  return parseQueueResponse(response.data);
}

export async function fetchQueueItem(id: string): Promise<QueueItem> {
  const response = await api.get(`/resolve/${id}`, RESPONSE_CONFIG);
  return response.data;
}

export async function fetchP1InterruptionDecision(): Promise<P1InterruptionDecision> {
  const response = await api.get('/resolve/interruptions/p1', RESPONSE_CONFIG);
  return response.data;
}

export async function fetchQueueStats(): Promise<QueueStats> {
  const response = await api.get('/resolve/stats', RESPONSE_CONFIG);
  return parseQueueStats(response.data);
}

export async function approveItem(id: string): Promise<QueueItem> {
  const response = await api.post(`/resolve/${id}/approve`, undefined, RESPONSE_CONFIG);
  return response.data;
}

export async function dismissItem(id: string, reason: string): Promise<QueueItem> {
  const response = await api.post(`/resolve/${id}/dismiss`, { reason }, RESPONSE_CONFIG);
  return response.data;
}

export async function resolveItem(id: string, resolution: string): Promise<QueueItem> {
  const response = await api.post(`/resolve/${id}/resolve`, { resolution }, RESPONSE_CONFIG);
  return response.data;
}

// ── Correlation Cluster Types ──

/** A group of correlated alerts that have been auto-correlated into an alert storm. */
export interface CorrelationCluster {
  id: string;
  customerId?: string;
  customerName?: string;
  highestSeverity: 'info' | 'warning' | 'critical' | 'emergency';
  alertCount: number;
  uniqueDevices: number;
  windowStart: string;
  windowEnd: string;
  status: 'open' | 'closed';
  ticketId?: string;
  serviceIntelligencePattern?: string;
  /** List responses omit member details. Absence is not an empty observation. */
  members?: CorrelationMember[];
}

export interface CorrelationMember {
  alertId: string;
  deviceName: string;
  alertTitle: string;
  severity: string;
  firedAt: string;
}

const correlationGroupSchema = z.object({
  id: z.string(),
  customerId: z.string().optional(),
  customerName: z.string().optional(),
  highestSeverity: z.enum(['info', 'warning', 'critical', 'emergency']),
  alertCount: z.number().int().nonnegative(),
  uniqueDevices: z.number().int().nonnegative(),
  windowStart: z.string().datetime({ offset: true }),
  windowEnd: z.string().datetime({ offset: true }),
  status: z.enum(['open', 'closed']),
  ticketId: z.string().optional(),
  serviceIntelligencePattern: z.string().optional(),
  members: z
    .array(
      z.object({
        alertId: z.string(),
        deviceName: z.string(),
        alertTitle: z.string(),
        severity: z.string(),
        firedAt: z.string().datetime({ offset: true }),
      })
    )
    .optional(),
});
const correlationGroupsSchema = z
  .object({
    groups: z.array(correlationGroupSchema).nullable(),
    total: z.number().int().nonnegative(),
  })
  .refine(
    ({ groups, total }) => (groups?.length ? total >= groups.length : total === 0),
    'Inconsistent correlation total'
  );

export async function fetchCorrelationClusters(): Promise<{
  clusters: CorrelationCluster[];
  total: number;
}> {
  const res = await api.get('/monitoring-alerts/correlation/groups', {
    params: { status: 'open', limit: 20 },
  });
  const body = unwrapOne(res.data, correlationGroupsSchema, 'Customer correlation groups');
  return { clusters: body.groups ?? [], total: body.total };
}

export async function promoteClusterToTicket(
  clusterId: string
): Promise<{ ticketId: string; ticketUrl: string }> {
  const res = await api.post(`/monitoring-alerts/correlation/groups/${clusterId}/promote-ticket`);
  return res.data;
}

// ── Constants ──

export const SEVERITY_COLORS: Record<QueueSeverity, string> = {
  critical: 'red-500',
  high: 'orange-500',
  medium: 'yellow-500',
  low: 'blue-500',
  info: 'gray-400',
};

export const SOURCE_LABELS: Record<SourceType, string> = {
  device_alert: 'Device Alert',
  backup: 'Backup',
  compliance: 'Compliance',
  credential: 'Credential',
  certificate: 'Certificate',
  patch: 'Patch',
  vendor: 'Vendor',
  perimeter: 'Perimeter',
  remediation_failed: 'Remediation Failed',
  m365: 'Microsoft 365',
};

export const STATUS_LABELS: Record<QueueStatus, string> = {
  open: 'Open',
  auto_resolved: 'Auto-Resolved',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  executing: 'Executing',
  resolved: 'Resolved',
  failed: 'Failed',
  dismissed: 'Dismissed',
};
